// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { Currency } from '@/currency/api/types';
import { IsoCurrencyCode } from '@/currency/constants/iso';
import { ProjectLineItem } from '@/project/api/types';

/**
 * The project's currency is the ISO shared by its set currency-bearing fields
 * (contract value, discount, and — once migrated — line items). Returns the
 * first set field's ISO, or null when none is set, so a new amount input can
 * default to the project's currency.
 */
export function deriveProjectCurrency(
  currencies: (Currency | null)[]
): IsoCurrencyCode | null {
  for (const value of currencies) {
    if (value !== null) {
      return value.isoCode;
    }
  }
  return null;
}

/**
 * Flattens the currency-bearing columns of each line item (unit price, fixed
 * price, total override) into a single list for deriveProjectCurrency.
 */
export function lineItemCurrencies(
  items:
    | Pick<ProjectLineItem, 'unitPrice' | 'fixedPrice' | 'totalOverride'>[]
    | undefined
): (Currency | null)[] {
  return (items ?? []).flatMap((item) => [
    item.unitPrice,
    item.fixedPrice,
    item.totalOverride,
  ]);
}

/** Tooltip explaining why a project currency selector is locked. */
export function projectCurrencyLockedTooltip(
  currencyCode: IsoCurrencyCode
): string {
  return `Project currency is locked to ${currencyCode}`;
}

/** Tooltip explaining a currency selector is locked by an invoiced charge. */
export const INVOICED_CHARGE_LOCKED_TOOLTIP =
  'Currency is locked because this line item has invoiced charges';

/** Tooltip explaining the total override must use the line item's price currency. */
export const TOTAL_OVERRIDE_CURRENCY_TOOLTIP =
  'Currency matches the line item price';

/** Tooltip explaining the project currency is frozen by an invoiced charge. */
export const PROJECT_INVOICED_CHARGE_LOCKED_TOOLTIP =
  'The project currency is locked because the project has invoiced charges';
